type AbortSignalAnyApi = { any: (signals: readonly AbortSignal[]) => AbortSignal };

function getAbortSignalAny(): AbortSignalAnyApi['any'] | null {
  const ctor = globalThis.AbortSignal as unknown as Partial<AbortSignalAnyApi>;
  return typeof ctor.any === 'function' ? ctor.any.bind(ctor) : null;
}

export interface ComposedAbortSignal {
  readonly signal: AbortSignal;
  // Detach any listeners registered by the fallback path. No-op for native
  // AbortSignal.any (the browser manages lifecycle internally). Callers MUST
  // invoke cleanup() when the operation completes (success or failure) to
  // avoid accumulating listeners on long-lived input signals.
  cleanup(): void;
}

export function composeAbortSignals(
  ...signals: readonly (AbortSignal | undefined)[]
): ComposedAbortSignal {
  const live = signals.filter((s): s is AbortSignal => s !== undefined);
  if (live.length === 0) {
    return { signal: new AbortController().signal, cleanup: () => {} };
  }
  if (live.length === 1) {
    return { signal: live[0] as AbortSignal, cleanup: () => {} };
  }

  const native = getAbortSignalAny();
  if (native !== null) {
    return { signal: native(live), cleanup: () => {} };
  }

  // Fallback per ambienti senza AbortSignal.any (target ES2024 lo supporta su
  // Chrome 116+/Firefox 124+/Safari 17.4+, ma happy-dom 20 potrebbe non averlo).
  // Pattern: registra UN listener per signal, esegue cleanup() quando uno
  // aborta OPPURE quando il caller chiama cleanup() esplicito (success path).
  // Senza il cleanup esplicito, listener si accumulerebbero su clientSignal
  // long-lived condiviso da molte chiamate.
  const controller = new AbortController();
  const handlers: { signal: AbortSignal; listener: () => void }[] = [];
  const cleanup = (): void => {
    for (const { signal, listener } of handlers) {
      signal.removeEventListener('abort', listener);
    }
    handlers.length = 0;
  };
  const onAbort = (sig: AbortSignal): void => {
    if (controller.signal.aborted) return;
    controller.abort(sig.reason);
    cleanup();
  };

  for (const s of live) {
    if (s.aborted) {
      onAbort(s);
      return { signal: controller.signal, cleanup };
    }
    const listener = (): void => onAbort(s);
    s.addEventListener('abort', listener, { once: true });
    handlers.push({ signal: s, listener });
  }
  return { signal: controller.signal, cleanup };
}

export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
      return;
    }
    let timer: ReturnType<typeof setTimeout> | null = null;
    const cleanup = (): void => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      signal?.removeEventListener('abort', onAbort);
    };
    const onAbort = (): void => {
      cleanup();
      reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
    };
    timer = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}
